import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, RefreshCwIcon, HomeIcon, ShoppingCartIcon } from 'lucide-react';
interface TransformLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (segment: 'vanzare' | 'inchiriere', leadData: {
    name: string;
    phone: string;
    email?: string;
    notes: string;
  }) => void;
  currentLead: {
    leadId: string;
    name: string;
    phone: string;
  };
}
export function TransformLeadModal({
  isOpen,
  onClose,
  onSubmit,
  currentLead
}: TransformLeadModalProps) {
  const [selectedSegment, setSelectedSegment] = useState<'vanzare' | 'inchiriere' | null>(null);
  const [name, setName] = useState(currentLead.name);
  const [phone, setPhone] = useState(currentLead.phone);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const handleSubmit = () => {
    if (!selectedSegment) {
      alert('Te rog selectează un segment (Vânzare sau Închiriere)');
      return;
    }
    if (!name.trim() || !phone.trim()) {
      alert('Te rog completează numele și telefonul');
      return;
    }
    onSubmit(selectedSegment, {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
      notes: notes.trim()
    });
    // Reset form
    setSelectedSegment(null);
    setName(currentLead.name);
    setPhone(currentLead.phone);
    setEmail('');
    setNotes('');
  };
  const handleClose = () => {
    setSelectedSegment(null);
    setName(currentLead.name);
    setPhone(currentLead.phone);
    setEmail('');
    setNotes('');
    onClose();
  };
  return <AnimatePresence data-id="element-5554">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-5555" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-5556">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden" data-id="element-5557">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-6" data-id="element-5558">
                <div className="flex items-center justify-between" data-id="element-5559">
                  <div className="flex items-center space-x-3" data-id="element-5560">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm" data-id="element-5561">
                      <RefreshCwIcon className="w-6 h-6" data-id="element-5562" />
                    </div>
                    <div data-id="element-5563">
                      <h2 className="text-2xl font-bold" data-id="element-5564">Transformă Lead</h2>
                      <p className="text-blue-100 text-sm" data-id="element-5565">
                        {currentLead.leadId} • {currentLead.name}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-xl transition-colors" data-id="element-5566">
                    <XIcon className="w-6 h-6" data-id="element-5567" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 space-y-6" data-id="element-5568">
                {/* Info Message */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl" data-id="element-5569">
                  <p className="text-sm text-blue-900" data-id="element-5570">
                    <strong data-id="element-5571">ℹ️ Transformare Lead:</strong> Clientul dorește
                    închiriere. Creează un lead nou și selectează segmentul
                    potrivit (Vânzare sau Închiriere).
                  </p>
                </div>

                {/* Segment Selection */}
                <div data-id="element-5572">
                  <label className="block text-sm font-bold text-slate-700 mb-3" data-id="element-5573">
                    Selectează Segment *
                  </label>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5574">
                    <motion.button type="button" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedSegment('vanzare')} className={`p-6 rounded-2xl border-2 transition-all ${selectedSegment === 'vanzare' ? 'border-emerald-500 bg-emerald-50 shadow-lg' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-5575">
                      <div className="flex flex-col items-center space-y-3" data-id="element-5576">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${selectedSegment === 'vanzare' ? 'bg-emerald-500' : 'bg-slate-100'}`} data-id="element-5577">
                          <ShoppingCartIcon className={`w-8 h-8 ${selectedSegment === 'vanzare' ? 'text-white' : 'text-slate-400'}`} data-id="element-5578" />
                        </div>
                        <div className="text-center" data-id="element-5579">
                          <h3 className={`font-bold text-lg ${selectedSegment === 'vanzare' ? 'text-emerald-700' : 'text-slate-700'}`} data-id="element-5580">
                            Vânzare
                          </h3>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-5581">
                            Lead pentru cumpărare proprietate
                          </p>
                        </div>
                      </div>
                    </motion.button>

                    <motion.button type="button" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} onClick={() => setSelectedSegment('inchiriere')} className={`p-6 rounded-2xl border-2 transition-all ${selectedSegment === 'inchiriere' ? 'border-blue-500 bg-blue-50 shadow-lg' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-5582">
                      <div className="flex flex-col items-center space-y-3" data-id="element-5583">
                        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${selectedSegment === 'inchiriere' ? 'bg-blue-500' : 'bg-slate-100'}`} data-id="element-5584">
                          <HomeIcon className={`w-8 h-8 ${selectedSegment === 'inchiriere' ? 'text-white' : 'text-slate-400'}`} data-id="element-5585" />
                        </div>
                        <div className="text-center" data-id="element-5586">
                          <h3 className={`font-bold text-lg ${selectedSegment === 'inchiriere' ? 'text-blue-700' : 'text-slate-700'}`} data-id="element-5587">
                            Închiriere
                          </h3>
                          <p className="text-xs text-slate-500 mt-1" data-id="element-5588">
                            Lead pentru închiriere proprietate
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  </div>
                </div>

                {/* Lead Details */}
                <div className="space-y-4 pt-4 border-t border-slate-200" data-id="element-5589">
                  <h3 className="text-sm font-bold text-slate-700" data-id="element-5590">
                    Detalii Lead Nou
                  </h3>

                  <div data-id="element-5591">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5592">
                      Nume Complet *
                    </label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="ex: Ion Popescu" className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-5593" />
                  </div>

                  <div data-id="element-5594">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5595">
                      Telefon *
                    </label>
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="ex: 0721234567" className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-5596" />
                  </div>

                  <div data-id="element-5597">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5598">
                      Email (opțional)
                    </label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ex: ion.popescu@email.com" className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" data-id="element-5599" />
                  </div>

                  <div data-id="element-5600">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-5601">
                      Notițe Transformare
                    </label>
                    <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Ex: Client inițial interesat de cumpărare, acum caută închiriere..." className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none" data-id="element-5602" />
                  </div>
                </div>

                {/* Warning Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-id="element-5603">
                  <p className="text-xs text-amber-900" data-id="element-5604">
                    <strong data-id="element-5605">⚠️ Important:</strong> Lead-ul original va fi marcat
                    ca "Transformat" și va fi creat un lead nou în segmentul
                    selectat cu datele actualizate.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-8 py-6 flex items-center justify-between border-t border-slate-200" data-id="element-5606">
                <button onClick={handleClose} className="px-6 py-3 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-5607">
                  Anulează
                </button>
                <button onClick={handleSubmit} disabled={!selectedSegment} className={`px-8 py-3 rounded-xl font-bold transition-all flex items-center space-x-2 ${selectedSegment ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:scale-105' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`} data-id="element-5608">
                  <RefreshCwIcon className="w-5 h-5" data-id="element-5609" />
                  <span data-id="element-5610">Transformă Lead</span>
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}